import { create } from 'zustand';
import { supabase } from '@/lib/supabase/client';
import type { DashboardStats, PipelineSummary, Lead, Deal, Activity, Reminder } from '@/types/crm';

interface DashboardStore {
  stats: DashboardStats | null;
  pipelineSummary: PipelineSummary[];
  recentLeads: Lead[];
  recentDeals: Deal[];
  upcomingActivities: Activity[];
  upcomingReminders: Reminder[];
  isLoading: boolean;
  dateRange: { from: string; to: string };
  fetchDashboardStats: () => Promise<void>;
  fetchPipelineSummary: () => Promise<void>;
  fetchRecentLeads: () => Promise<void>;
  fetchRecentDeals: () => Promise<void>;
  fetchUpcomingActivities: () => Promise<void>;
  fetchUpcomingReminders: () => Promise<void>;
  refreshAll: () => Promise<void>;
  setDateRange: (from: string, to: string) => void;
  subscribeToRealtime: () => () => void;
}

const getDefaultDateRange = () => {
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  return {
    from: firstDay.toISOString().split('T')[0],
    to: now.toISOString().split('T')[0],
  };
};

export const useDashboardStore = create<DashboardStore>((set, get) => ({
  stats: null,
  pipelineSummary: [],
  recentLeads: [],
  recentDeals: [],
  upcomingActivities: [],
  upcomingReminders: [],
  isLoading: false,
  dateRange: getDefaultDateRange(),

  fetchDashboardStats: async () => {
    set({ isLoading: true });
    try {
      // Pass null for no date filtering - show all-time stats
      const { data, error } = await supabase.rpc('crm_get_dashboard_stats', {
        p_date_from: null,
        p_date_to: null,
      });

      if (error) {
        console.error('fetchDashboardStats error:', error);
        return;
      }
      set({ stats: data });
    } finally {
      set({ isLoading: false });
    }
  },

  fetchPipelineSummary: async () => {
    const { data, error } = await supabase.rpc('crm_get_pipeline_summary');
    if (error) {
      console.error('fetchPipelineSummary error:', error);
      set({ pipelineSummary: [] });
      return;
    }
    set({ pipelineSummary: data || [] });
  },

  fetchRecentLeads: async () => {
    const { data, error } = await supabase
      .from('crm_leads_with_details')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(5);
    if (error) {
      console.error('fetchRecentLeads error:', error);
      set({ recentLeads: [] });
      return;
    }
    set({ recentLeads: data || [] });
  },

  fetchRecentDeals: async () => {
    const { data, error } = await supabase
      .from('crm_deals_with_details')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(5);
    if (error) {
      console.error('fetchRecentDeals error:', error);
      set({ recentDeals: [] });
      return;
    }
    set({ recentDeals: data || [] });
  },

  fetchUpcomingActivities: async () => {
    const today = new Date().toISOString().split('T')[0];
    const { data, error } = await supabase
      .from('crm_activities')
      .select('*')
      .eq('is_completed', false)
      .gte('due_date', today)
      .order('due_date', { ascending: true })
      .limit(5);
    if (error) {
      console.error('fetchUpcomingActivities error:', error);
      set({ upcomingActivities: [] });
      return;
    }
    set({ upcomingActivities: data || [] });
  },

  fetchUpcomingReminders: async () => {
    const { data, error } = await supabase
      .from('crm_reminders')
      .select('*')
      .eq('status', 'pending')
      .order('due_date', { ascending: true })
      .limit(10);
    if (error) {
      console.error('fetchUpcomingReminders error:', error);
      set({ upcomingReminders: [] });
      return;
    }
    set({ upcomingReminders: data || [] });
  },

  refreshAll: async () => {
    const { fetchDashboardStats, fetchPipelineSummary, fetchRecentLeads, fetchRecentDeals, fetchUpcomingActivities, fetchUpcomingReminders } = get();
    await Promise.all([
      fetchDashboardStats(),
      fetchPipelineSummary(),
      fetchRecentLeads(),
      fetchRecentDeals(),
      fetchUpcomingActivities(),
      fetchUpcomingReminders(),
    ]);
  },

  setDateRange: (from: string, to: string) => set({ dateRange: { from, to } }),

  subscribeToRealtime: () => {
    const { refreshAll } = get();
    
    const leadsChannel = supabase
      .channel('crm_leads_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'crm_leads' }, () => {
        refreshAll();
      })
      .subscribe();

    const dealsChannel = supabase
      .channel('crm_deals_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'crm_deals' }, () => {
        refreshAll();
      })
      .subscribe();

    const activitiesChannel = supabase
      .channel('crm_activities_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'crm_activities' }, () => {
        refreshAll();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(leadsChannel);
      supabase.removeChannel(dealsChannel);
      supabase.removeChannel(activitiesChannel);
    };
  },
}));
